import * as React from 'react';
import { Button, Card, Checkbox, DatePicker, Form, Input, Select, Spin, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { createReport } from './api';
import HistoryTable, { HISTORY_QUERY_KEY } from './HistoryTable';

type FormValues = {
    platform: string;
    account: string;
    all_time?: boolean;
    month?: Dayjs;
};

const SocialMetricsPage: React.FC = () => {
    const [form] = Form.useForm<FormValues>();
    const allTime = Form.useWatch('all_time', form);
    const navigate = useNavigate();
    const qc = useQueryClient();
    const create = useMutation({
        mutationFn: createReport,
        onSuccess: (report) => {
            qc.invalidateQueries({ queryKey: HISTORY_QUERY_KEY });
            navigate(`/reporting/social-metrics/${report.id}`);
        },
    });

    const onFinish = (values: FormValues) => {
        create.mutate({
            platform: values.platform,
            account: values.account.trim(),
            month: values.all_time ? undefined : values.month?.format('YYYY-MM'),
        });
    };

    return (
        <div>
            <Typography.Title level={3} style={{ marginTop: 0 }}>Social metrics</Typography.Title>
            <Typography.Paragraph type="secondary">
                One platform, one account, and a month or all time. The report is saved here. Excel is only generated when you download it.
            </Typography.Paragraph>
            <Spin spinning={create.isLoading} tip="This will take several minutes. Please wait.">
                <Card>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{ platform: 'youtube' }}
                        disabled={create.isLoading}
                    >
                        <Form.Item name="platform" label="Platform" rules={[{ required: true, message: 'Choose a platform' }]}>
                            <Select
                                options={[
                                    { value: 'youtube', label: 'YouTube' },
                                    { value: 'tiktok', label: 'TikTok' },
                                    { value: 'instagram', label: 'Instagram' },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="account"
                            label="Account"
                            rules={[{ required: true, message: 'Enter a handle, channel ID, or profile URL' }]}
                        >
                            <Input placeholder="@handle, channel ID, or profile URL" />
                        </Form.Item>
                        <Form.Item name="all_time" valuePropName="checked">
                            <Checkbox>All time</Checkbox>
                        </Form.Item>
                        <Form.Item
                            name="month"
                            label="Month"
                            rules={allTime ? [] : [{ required: true, message: 'Choose a month' }]}
                        >
                            <DatePicker picker="month" disabled={!!allTime} style={{ width: 200 }} disabledDate={(d) => d.isAfter(dayjs(), 'month')} />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={create.isLoading}>
                            Create report
                        </Button>
                    </Form>
                </Card>
            </Spin>
            <HistoryTable />
        </div>
    );
};

export default SocialMetricsPage;
